import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { Form, Card, Flex, Select, Input, Typography, Button } from "antd";
import {
  CalendarDaysIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { formatDate } from "../../utils/helpers";
import { generateDisplayId } from "../../utils/displayId";
import { SpeciesList } from "../../utils/const";
import { Capture } from "../../types/api/models/capture";
import { Detection } from "../../types";

const { Text } = Typography;
const { TextArea } = Input;

interface SpecimenFormProps {
  item: Capture | Detection;
  id: string | undefined;
  isMeshRoute: boolean;
  onSubmit: (values: { species?: string; notes?: string }) => Promise<void>;
  isSubmitting: boolean;
}

const SpecimenForm: React.FC<SpecimenFormProps> = ({
  item,
  id,
  isMeshRoute,
  onSubmit,
  isSubmitting,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const speices = Form.useWatch("species", form);
  const speciesBorderClass = !speices ? "border-[#F97316] !bg-[#FFF7ED]" : "";

  const speciesOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    SpeciesList.forEach((genusGroup) => {
      genusGroup.species.forEach((speciesName) => {
        const fullLabel = `${genusGroup.genus} ${speciesName}`;
        options.push({ label: fullLabel, value: fullLabel });
      });
    });
    options.push({ label: "Unknown", value: "Unknown" });
    return options;
  }, []);

  useEffect(() => {
    if (item) {
      form.setFieldsValue({
        species:
          "contributed_species" in item
            ? (item.contributed_species ?? undefined)
            : undefined,
        notes: item.notes ?? "",
      });
    }
  }, [item, form]);

  const imageId = useMemo(() => {
    const timestamp =
      item.created_at || ("captured_at" in item ? item.captured_at : undefined);
    const captureUuid = isMeshRoute
      ? id
      : "capture_uuid" in item
        ? item.capture_uuid
        : undefined;
    return generateDisplayId({
      captureUuid,
      specimenUuid: isMeshRoute ? undefined : id,
      timestamp,
    });
  }, [item, id, isMeshRoute]);

  type FieldRow =
    | {
        label: string;
        value: string | undefined;
        icon?: ReactNode;
        content?: never;
      }
    | { label: string; content: ReactNode; icon?: never; value?: never };

  const fieldRows = useMemo<FieldRow[]>(() => {
    const timestamp =
      item.created_at || ("captured_at" in item ? item.captured_at : undefined);

    const meshId = isMeshRoute
      ? `Mesh_${item?.uuid?.slice(-6)}`
      : "capture_uuid" in item
        ? `Mesh_${item?.capture_uuid?.slice(-6)}`
        : undefined;

    const meshLink = isMeshRoute
      ? id
      : "capture_id" in item
        ? item.capture_id
        : undefined;

    return [
      { label: "Image ID", value: imageId },
      {
        label: "Mesh ID",
        content: meshId ? (
          <span
            onClick={() => navigate(`/mesh/${meshLink}`)}
            className=" hover:text-[#003f60] break-all cursor-pointer"
          >
            {meshId}
          </span>
        ) : (
          <Text className="m-0">—</Text>
        ),
      } satisfies FieldRow,
      {
        label: "Image timestamp",
        value: formatDate(timestamp),
        icon: <CalendarDaysIcon className="w-5 h-5" />,
      },
      ...(!isMeshRoute
        ? [
            {
              label: "Species",
              content: (
                <Form.Item name="species" className="m-0! w-full pt-6">
                  <Select
                    variant="borderless"
                    showSearch
                    allowClear
                    placeholder="Select species"
                    options={speciesOptions}
                    className="w-full select-species"
                  />
                </Form.Item>
              ),
            } satisfies FieldRow,
          ]
        : []),
    ];
  }, [isMeshRoute, id, item, imageId, speciesOptions, navigate]);

  return (
    <div className="flex-1">
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        className="h-full flex flex-col"
      >
        <Card
          className="bg-[#FDFDFD] h-full"
          styles={{ body: { padding: "16px 24px" } }}
        >
          <Flex vertical gap="middle">
            {fieldRows.map(({ label, value, content, icon }) => (
              <div
                key={label}
                className={`flex border rounded-md overflow-hidden ${label === "Species" && speciesBorderClass}`}
              >
                <div
                  className={`flex items-center gap-2 p-4 bg-white w-1/3 border-r border-[#0C4A6E] ${label === "Species" && speciesBorderClass}`}
                >
                  {icon}
                  <Text className="font-semibold m-0">{label}</Text>
                </div>
                <div
                  className={`flex items-center bg-white w-2/3 ${content ? "px-4 py-1" : "p-4"} ${label === "Species" && speciesBorderClass}`}
                >
                  {content ?? <Text className="m-0">{value}</Text>}
                </div>
              </div>
            ))}

            <div className="flex items-start gap-2 p-4 border border-[#0C4A6E] rounded-md bg-white mb-2">
              <PencilSquareIcon className="w-5 h-5  mt-[8px]" />
              <Text className=" font-semibold m-0 whitespace-nowrap mt-[8px]">
                Notes:
              </Text>
              <Form.Item name="notes" className="m-0 w-full flex-1 pt-1! mb-1!">
                <TextArea
                  variant="borderless"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  placeholder="Add notes here..."
                  className="w-full p-0 leading-relaxed "
                />
              </Form.Item>
            </div>

            <Form.Item className="m-0 mt-auto flex justify-center">
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="bg-[#005F8F] h-10"
              >
                Submit Changes
              </Button>
            </Form.Item>
          </Flex>
        </Card>
      </Form>
    </div>
  );
};

export default SpecimenForm;
